// Complete CollapsibleIdConfigForm with ALL form fields properly defined

import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/ui/collapsible";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useEntityOptions,
  useUtilityOptions,
  useDateFormatOptions,
  useSeparatorOptions,
  useCreateIdConfigMutation,
  useUpdateIdConfigMutation,
} from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";
import { NumberFormat } from "../../../types";
import { toast } from "@shared/hooks/use-toast";

type IdFormData = {
  entity: string;
  utility: string;
  prefix: string;
  dateElement: string;
  separator: string;
  sequenceLength: number;
  startingNumber: number;
  currentNumber: number;
};

interface CollapsibleIdConfigFormProps {
  editingData?: NumberFormat | null;
  onClose: () => void;
  selectedIdType: "master" | "transaction" | "";
}

const CollapsibleIdConfigForm = ({
  editingData,
  onClose,
  selectedIdType,
}: CollapsibleIdConfigFormProps) => {
  const [isOpen, setIsOpen] = useState(true);
  const [preview, setPreview] = useState("");
  const remoteUtilityId = useRemoteUtilityId();

  const createIdConfigMutation = useCreateIdConfigMutation();
  const updateIdConfigMutation = useUpdateIdConfigMutation();

  // Get options from APIs
  const entityOptions = useEntityOptions(selectedIdType);
  const utilityOptions = useUtilityOptions();
  const dateFormatOptions = useDateFormatOptions();
  const separatorOptions = useSeparatorOptions();

  // COMPLETE form fields configuration - ALL fields must be defined here
  const formFields: FormField[] = [
    {
      name: "entity",
      label: "Entity",
      type: "select",
      required: true,
      placeholder: "Select Entity",
      options: entityOptions,
      helperText:
        "Choose the utility object for which the ID is being generated.",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        select:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
    },
    {
      name: "sequenceLength",
      label: "Sequence Length",
      type: "number",
      required: true,
      min: 1,
      max: 10,
      placeholder: "4",
      helperText: "Number of digits in sequence like 0001.",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
    },
    {
      name: "prefix",
      label: "Prefix (Optional)",
      type: "text",
      required: false,
      placeholder: "e.g., BILL, MTR",
      maxLength: 10,
      helperText: "Short code like BILL, MTR, etc.",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
      customValidation: (value: any) => {
        const alphaRegex = /^[A-Za-z\s]+$/;

        // if optional and empty, it's valid
        if (!value) return true;

        // otherwise, validate regex
        return alphaRegex.test(value) ? true : "Only alphabets are allowed";
      },
    },
    {
      name: "utility",
      label: "Utility Service (Optional)",
      type: "select",
      required: false,
      placeholder: "Select Utility Service",
      options: utilityOptions,
      helperText: "Specify which utility service this ID belongs to.",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        select:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
    },
    {
      name: "startingNumber",
      label: "Starting Number",
      type: "number",
      required: false,
      min: 1,
      placeholder: "1",
      helperText: "Initial number in the sequence.",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
    },
    {
      name: "currentNumber",
      label: "Current Number",
      type: "number",
      required: false,
      min: 1,
      placeholder: "1",
      helperText: "Next number to be used in the sequence.",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        input:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
    },
    {
      name: "dateElement",
      label: "Date Element (Optional)",
      type: "select",
      required: false,
      placeholder: "Select Date Format",
      options: dateFormatOptions,
      helperText: "Adds date info like 2404 (April 2024).",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        select:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
    },
    {
      name: "separator",
      label: "Separator (Optional)",
      type: "select",
      required: false,
      placeholder: "Select Separator",
      options: separatorOptions,
      helperText: "Used to visually separate parts of the ID.",
      classes: {
        container: "w-full",
        label: "text-sm font-medium text-gray-900",
        select:
          "w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-sm text-gray-500 mt-1",
      },
    },
  ];

  // Create form instance
  const idConfigForm = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Watch form values for preview
  const watchedValues = idConfigForm.watch();

  // Set form values when editing existing data
  useEffect(() => {
    if (editingData) {
      // Map API data to form data using the actual API structure
      const formData = {
        entity: editingData.submoduleId?.toString() || "",
        utility: editingData.utilityService || "",
        prefix: editingData.prefix || "",
        dateElement: editingData.dateElement || "",
        separator: editingData.separator || "",
        sequenceLength: editingData.length || 4,
        startingNumber: editingData.startingno || 1,
        currentNumber: editingData.currentno || 1,
      };

      // Set form values
      Object.entries(formData).forEach(([key, value]) => {
        idConfigForm.setValue(key as keyof IdFormData, value);
      });
    } else {
      // Set default values for new ID
      idConfigForm.setValue("separator", "-");
      idConfigForm.setValue("sequenceLength", 4);
      idConfigForm.setValue("startingNumber", 1);
      idConfigForm.setValue("currentNumber", 1);
    }
  }, [editingData, idConfigForm]);

  // Update preview whenever form data changes
  useEffect(() => {
    updatePreview();
  }, [watchedValues]);

  const updatePreview = () => {
    let result = "";

    // Add utility code if present
    if (watchedValues.utility) {
      result += watchedValues.utility;
      if (watchedValues.prefix) {
        result += watchedValues.separator || "-";
      }
    }

    if (watchedValues.prefix) {
      result += watchedValues.prefix;
    }

    if (
      watchedValues.separator &&
      watchedValues.dateElement &&
      watchedValues.dateElement !== "None" &&
      watchedValues.dateElement !== ""
    ) {
      result += watchedValues.separator;

      // Simulate date element
      const currentDate = new Date();
      const yearFull = currentDate.getFullYear().toString(); // e.g., "2025"
      const yearShort = yearFull.slice(-2); // e.g., "25"
      const month = String(currentDate.getMonth() + 1).padStart(2, "0"); // e.g., "08"

      switch (watchedValues.dateElement) {
        case "YY":
          result += yearShort;
          break;
        case "YYYY":
          result += yearFull;
          break;
        case "YYMM":
          result += `${yearShort}${month}`;
          break;
        case "YYYYMM":
          result += `${yearFull}${month}`;
          break;
        default:
          break;
      }
    }

    if (watchedValues.separator && watchedValues.sequenceLength > 0) {
      result += watchedValues.separator;
      const sequenceLength = watchedValues.sequenceLength || 4;
      result += "0".repeat(sequenceLength - 1) + "1";
    }

    setPreview(result);
  };

  const handleSubmit = async (formData: any) => {
    try {
      const isValid = await FormService.validateForm(idConfigForm);
      if (!isValid) return;

      // Find the selected entity from the list
      const selectedEntity = entityOptions.find(
        (opt) => opt.value === formData.entity
      );

      if (!selectedEntity) {
        return;
      }

      const payload = {
        configuration_type: selectedIdType === "master" ? 0 : 1,
        module: Number(selectedEntity.meta.moduleId),
        submodule: selectedEntity.value,
        utility_service: formData.utility,
        utility: remoteUtilityId,
        length: Number(formData.sequenceLength),
        prefix: formData.prefix || "",
        separator: formData.separator || "",
        startingno: Number(formData.startingNumber ?? 1),
        currentno: Number(formData.currentNumber ?? 1),
        date_element: formData.dateElement || "",
        format_preview: preview,
      };

      // Use different mutation based on whether we're editing or creating
      if (editingData) {
        await updateIdConfigMutation.mutateAsync(
          { id: editingData.id, payload },
          {
            onSuccess: () => {
              onClose();
              setIsOpen(false);
            },
          }
        );
      } else {
        await createIdConfigMutation.mutateAsync(payload, {
          onSuccess: () => {
            onClose();
            setIsOpen(false);
            logEvent("Id Congig Created", {
              ...payload,
            });
          },
        });
      }
    } catch (error) {
      toast({ title: "Error", description: error.message });
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    // Transform prefix to uppercase
    if (fieldName === "prefix" && typeof value === "string") {
      const upperValue = value.toUpperCase();
      if (upperValue !== value) {
        idConfigForm.setValue(fieldName, upperValue);
      }
    }
  };

  return (
    <div className="bg-white border rounded-lg shadow-sm my-6">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium">
            {editingData
              ? "Edit ID Configuration"
              : `New ${
                  selectedIdType.charAt(0).toUpperCase() +
                  selectedIdType.slice(1)
                } ID Configuration`}
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent>
          <div className="p-4">
            {/* Live Preview */}
            <div className="grid gap-2 bg-gray-50 p-4 rounded-md mb-6">
              <Label>Live Preview</Label>
              <div className="p-3 bg-white border border-gray-200 rounded text-center">
                <code className="text-lg font-mono">
                  {preview || "Configure ID to see preview"}
                </code>
              </div>
            </div>

            {/* Dynamic Form */}
            <DynamicForm
              fields={formFields}
              form={idConfigForm}
              onSubmit={handleSubmit}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                layout: "vertical",
                spacing: "medium",
                showSubmitButton: false,
                showResetButton: false,
                classes: {
                  form: "space-y-6",
                  fieldContainer: "w-full",
                },
              }}
            />
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-2 p-4 ">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => handleSubmit(idConfigForm.getValues())}
              disabled={!idConfigForm.formState.isValid}
            >
              {editingData ? "Update" : "Create"} ID
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default CollapsibleIdConfigForm;
